import fetch from 'isomorphic-fetch';
import { get } from 'lodash';
import errorHandler from 'lib/utils/errorHandler';
import { isUserLoggedIn } from 'apps/user/helpers';
import { config } from 'api';

import { BetaFeatures as FoodProfileBetaFeatures } from 'apps/foodProfile/consts';

const FM_TOKEN_URL = `${config.apiHost}/webshop/api/v2/fm-token`;
const FM_PROFILESTATUS_URL = `${config.apiHost}/webshop/api/v1/food-profile`;

export const SET_FOODPROFILE = 'SET_FOODPROFILE';
export const SET_FOODPROFILE_STATUS = 'SET_FOODPROFILE_STATUS';
export const SET_FM_TOKEN = 'SET_FM_TOKEN';
export const TOGGLE_FOODPROFILE_BADGE = 'TOGGLE_FOODPROFILE_BADGE';
export const FETCH_FOODPROFILE = 'FETCH_FOODPROFILE';
export const SET_FOODPROFILE_CONDITIONS = 'SET_FOODPROFILE_CONDITIONS';
export const SET_PRODUCT_SUITABILITY = 'SET_PRODUCT_SUITABILITY';
export const SHOW_MOBILE_SIDEBAR = 'SHOW_MOBILE_SIDEBAR';
export const HIDE_MOBILE_SIDEBAR = 'HIDE_MOBILE_SIDEBAR';

/**
 * Update Food Profile data in the application store
 * @param {FMProfile} data profile data to set
 */
export function setFoodProfile(data) {
  return {
    type: SET_FOODPROFILE,
    data,
  };
}

/**
 * Toggle Food Profile "flagged" badges visibility on products
 */
export function toggleFoodProfileBadge() {
  return {
    type: TOGGLE_FOODPROFILE_BADGE,
  };
}

export function showFoodProfileMobileSidebar() {
  return {
    type: SHOW_MOBILE_SIDEBAR,
  };
}

export function hideFoodProfileMobileSidebar() {
  return {
    type: HIDE_MOBILE_SIDEBAR,
  };
}

/**
 * Ensure that application state contains FoodProfile status for currently logged-in user
 */
export function ensureFoodProfileStatus() {
  return (dispatch, getState) => {
    const state = getState();
    const isLoggedIn = isUserLoggedIn(state.user);
    const isFoodMaestroEnabled = get(state, 'user.betaFeatures', [])
      .includes(FoodProfileBetaFeatures.FOODMAESTRO_ENABLED);

    if (!isFoodMaestroEnabled || !isLoggedIn) return Promise.reject('FoodMaestro is not enabled');

    const status = get(state, 'foodProfile.profileStatus');

    if (status) {
      return Promise.resolve();
    } else {
      return dispatch(fetchFoodProfileStatus());
    }
  };
}

/**
 * Fetch FoodProfile status for currently logged-in user
 */
export function fetchFoodProfileStatus() {
  return (dispatch, getState, { network }) => {
    const state = getState();
    const isLoggedIn = isUserLoggedIn(state.user);
    const isFoodMaestroEnabled = get(state, 'user.betaFeatures', [])
      .includes(FoodProfileBetaFeatures.FOODMAESTRO_ENABLED);

    if (!isFoodMaestroEnabled || !isLoggedIn) return Promise.reject('FoodMaestro is not enabled');

    return network.get(FM_PROFILESTATUS_URL)
      .then(({ status }) => {
        if (status === 'NONE' || status === 'NEW') {
          dispatch(setFoodProfile(null));
        }
        return dispatch(setFoodProfileStatus(status));
      })
      .catch(errorHandler);
  };
}

/**
 * Ensure that application state contains FoodMaestro token for currently logged-in user
 */
export function ensureFMToken(createIfMissing = false) {
  return (dispatch, getState) => {
    const state = getState();
    const token = get(state, 'foodProfile.token');

    if (token) {
      return Promise.resolve();
    } else {
      return dispatch(ensureFoodProfileStatus())
        .then(
          () => dispatch(fetchFMToken(createIfMissing)),
          () => undefined // do nothing in case fetchFoodProfileStatus rejected
        );
    }
  };
}

/**
 * Fetch FoodMaestro token for currently logged-in user
 */
export function fetchFMToken(createIfMissing = false) {
  return (dispatch, getState, { network }) => {
    const state = getState();
    const profileStatus = get(state, 'foodProfile.profileStatus');

    if (profileStatus !== 'NONE') {
      return network.get(FM_TOKEN_URL, { headers: { Accept: 'text/plain' } })
        .then(data => dispatch(setFMToken(data)))
        .catch(errorHandler);
    } else {
      if (createIfMissing) {
        return network.post(FM_TOKEN_URL, { headers: { Accept: 'text/plain' } })
          .then(data => dispatch(setFMToken(data)))
          .catch(errorHandler);
      }
      return Promise.reject();
    }
  };
}

/**
 * Update FoodProfile status in the application store
 */
export function setFoodProfileStatus(status) {
  return {
    type: SET_FOODPROFILE_STATUS,
    data: status,
  };
}

/**
 * Update FoodMaestro token in the application store
 */
export function setFMToken(data) {
  return {
    type: SET_FM_TOKEN,
    data,
  };
}

/**
 * Ensure that application state contains Food Profile data for currently logged-in user
 */
export function ensureFoodProfile() {
  return (dispatch, getState) => {
    const state = getState();
    const profile = get(state, 'foodProfile.profile');

    if (profile) {
      return Promise.resolve();
    } else {
      return dispatch(ensureFMToken())
        .then(() => dispatch(fetchFoodProfile()));
    }
  };
}

/**
 * Fetch Food Profile data from FoodMaestro servers
 */
export function fetchFoodProfile() {
  return (dispatch, getState) => {
    const { token } = getState().foodProfile;
    return fetch(`${config.fmApiUrl}getprofileconditions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.success === false) {
          dispatch(setFoodProfile(null));
        } else {
          dispatch(setFoodProfile(response));
        }
      })
      .catch((e) => {
        dispatch(setFoodProfile(null));
        errorHandler(e);
      });
  };
}

/**
 * Persist data on FoodMaestro servers and updates application store
 * @param {FMProfile} data profile data to persist
 */
export function persistFoodProfile(data) {
  return (dispatch, getState) => {
    const state = getState();
    const token = get(state, 'foodProfile.token');
    const productsBySku = get(state, 'products.productsBySku', {});
    const barcodes = Object.values(productsBySku).map(product => product.gtin);

    return fetch(`${config.fmApiUrl}updateprofileconditions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        profileid: data.ProfileID,
        conditions: data.Conditions.map((v) => ({
          conditionid: v.ConditionID,
          selected: v.ConditionSelected,
        })),
      }),
    })
      .then((response) => response.json())
      .then(
        (response) => {
          if (response.Success) {
            dispatch(setFoodProfile([data]));
            // get productsSuitability data
            dispatch(fetchProductsSuitability(barcodes));
          } else {
            throw new Error('Cannot set profile conditions');
          }
        },
      );
  };
}

/**
 * Ensure that application state contains FoodProfile Conditions for currently logged-in user
 */
export function ensureFoodProfileConditions() {
  return (dispatch, getState) => {
    const state = getState();
    const profileConditions = get(state, 'foodProfile.profileConditions');

    if (profileConditions) {
      return Promise.resolve();
    } else {
      return dispatch(ensureFMToken(true))
        .then(() => dispatch(fetchFoodProfileConditions()));
    }
  };
}

/**
 * Fetch a list of supported conditions from FoodMaestro servers
 */
export function fetchFoodProfileConditions() {
  return (dispatch, getState) => {
    const { token } = getState().foodProfile;

    return fetch(`${config.fmApiUrl}getconditions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    })
      .then((response) => response.json())
      .then(
        (data) => dispatch(setFoodProfileConditions(data.Conditions)),
        errorHandler
      );
  };
}

/**
 * Update a list of supported conditions in the application store
 */
export function setFoodProfileConditions(data) {
  return {
    type: SET_FOODPROFILE_CONDITIONS,
    data,
  };
}

/**
 * Creates FoodMaestro profile (for new users)
 * @param {FMProfile} data Object containing FoodMaestro Profile data
 */
export function createFoodProfile(data) {
  return (dispatch, getState, { network }) => {
    const { token } = getState().foodProfile;
    return fetch(`${config.fmApiUrl}authenticate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    })
      .then((response) => response.json())
      .then(
        (response) => {
          const profileId = get(response, ['Profiles', '0', 'ProfileID']);
          if (profileId) {
            const profileWithConditions = {
              ...data,
              ProfileID: profileId,
            };

            return network.put(FM_PROFILESTATUS_URL, {
              body: JSON.stringify({ status: 'ACTIVE' }),
            }).then(
              () => dispatch(persistFoodProfile(profileWithConditions))
            );
          } else {
            throw new Error('Cannot create profile');
          }
        }
      );
  };
}

export function initProductsSuitability() {
  return (dispatch, getState) => dispatch(ensureFoodProfileStatus())
    .then(() => {
      const state = getState();
      const profileStatus = get(state, 'foodProfile.profileStatus');

      if (profileStatus === 'ACTIVE') {
        const { productsBySku } = getState().products;
        const barcodes = Object.values(productsBySku).map(product => product.gtin);

        return dispatch(fetchProductsSuitability(barcodes)).then(
          () => dispatch(ensureFoodProfile())
        );
      } else return Promise.resolve();
    });
}

/**
 * Fetch suitability data for given products (by barcode)
 * @param {Array[string]} data Array of product's barcodes to fetch suitability data for
 */
export function fetchProductsSuitability(data = []) {
  return (dispatch, getState) => {
    if (data.length === 0) return Promise.resolve();
    return dispatch(ensureFMToken()).then(() => {
      const { foodProfile = {} } = getState();
      const { token, profileStatus } = foodProfile;

      if (profileStatus === 'ACTIVE' && token && data.length > 0) {
        fetch(`${config.fmApiUrl}checkproductsuitability`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            products: data,
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            const suitabilityByBarcode = response.reduce((acc, val) => {
              const suitability = get(val, 'Suitability[0]', {});
              const barcode = get(val, 'Product[0].BarcodeNumber');
              acc[barcode] = {
                isSuitable: suitability.isSuitable,
                reason: suitability.Reason,
                unsuitableConditions: suitability.UnsuitableConditions || [],
                containIngredients: suitability.ContainIngredients || [],
                mayContainIngredients: suitability.MayContainIngredients || [],
              };
              return acc;
            }, {});
            dispatch(setProductSuitability(suitabilityByBarcode));
          })
          .catch(errorHandler);
      }
    });
  };
}

/**
 * Update products suitability data in the application store
 * @param {Object.<string, FPProductSuitability>} data Products suitability data (by barcode)
 */
export function setProductSuitability(data) {
  return {
    type: SET_PRODUCT_SUITABILITY,
    data,
  };
}

/**
 * @typedef FMProfile
 * @type {object}
 * @property {string} ProfileID profile ID (GUID)
 * @property {FMProfileCondition[]} Conditions profile conditions
 */

/**
 * @typedef FMProfileCondition
 * @type {Object}
 * @property {string} ConditionID condition ID (GUID)
 * @property {number} ConditionSelected is condition selected flag (1 - selected, 0 - unselected)
 * @property {string} ConditionTypeName human-readable condition type name
 * @property {string} ConditionGroupName human-readable condition group name
 * @property {string} ConditionName human-readable condition name
 */

/**
 * @typedef FPProductSuitability
 * @type {object}
 * @property {number} isSuitable suitability flag (1 - suitable, 0 - non-suitable, -1 - unknown suitability)
 * @property {string} Reason human readable suitability description
 */
